/**
 * Asking for a scope-of-work draft, from the browser's side.
 *
 * The browser sends words and gets words back. The OpenAI key lives in the
 * `scope-draft` Edge Function (rule 7), which builds the whole prompt itself;
 * what comes back is the model's raw answer, and `parseScopeNote` in core
 * decides what of it is allowed into a line item's note. Nothing here touches
 * the database — the draft lands in the note box, where the rep edits it and
 * the line editor saves it the way it saves everything (ADR 0048).
 */

import { parseScopeNote, scopeDraftBlocker } from "@roof-hawk/core";

import { supabase } from "./supabase";

export class ScopeDraftError extends Error {}

/**
 * Call the function, keeping the sentence it sent back.
 *
 * `functions.invoke` reports a non-2xx as a generic error and hides the body
 * on `context`. The body is where the useful sentence is — "the key is not
 * set up", "the service is busy" — and a rep shown "non-2xx status code"
 * instead has been told nothing at all.
 */
async function invoke(
  body: Record<string, unknown>,
): Promise<{ text: string; qualifiers: string[] }> {
  const { data, error } = await supabase.functions.invoke("scope-draft", {
    body,
  });
  if (!error) {
    const answer = data as { text: string; qualifiers?: unknown };
    return {
      text: answer.text,
      // Ids the function resolved from its own catalogue. A response from
      // before ADR 0100 — or one where the clause read failed — carries none,
      // and the line simply keeps no clauses.
      qualifiers: Array.isArray(answer.qualifiers)
        ? answer.qualifiers.filter(
            (id): id is string => typeof id === "string" && id.trim() !== "",
          )
        : [],
    };
  }

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const payload = await context.json().catch(() => null);
    if (payload && typeof payload.error === "string") {
      throw new ScopeDraftError(payload.error);
    }
  }

  throw new ScopeDraftError(
    "Could not reach the drafting service. Check the signal and try again.",
  );
}

/**
 * Turn what the rep said into the scope of work for the line being edited.
 *
 * Comes back with the protective clauses the model chose, as
 * `scope_qualifiers.id` values (ADR 0100). They are stored on the line, not
 * pasted into the note: the customer reads each one once at the foot of the
 * package, and the wording stays Jonathon's to edit in Tools.
 */
export async function draftScopeNote(
  transcript: string,
  context: { lineName: string | null; trade: string | null },
): Promise<{ note: string; qualifiers: string[] }> {
  const blocker = scopeDraftBlocker(transcript);
  if (blocker) throw new ScopeDraftError(blocker);

  const { text, qualifiers } = await invoke({
    transcript: transcript.trim(),
    lineName: context.lineName,
    trade: context.trade,
  });

  const note = parseScopeNote(text);
  if (!note) {
    throw new ScopeDraftError(
      "The draft came back empty. Try again — the second one usually lands.",
    );
  }
  return { note, qualifiers };
}
